import React, { useEffect, useState } from "react";
import { ym } from "../lib/dates";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Printer, Download, Users, GraduationCap, Buildings, Receipt, WhatsappLogo, Tag, Warning } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api, { formatIDR, formatApiError, formatMonth } from "../lib/api";
import { generatePDF } from "../lib/pdf";

const TABS = [
  { key: "siswa", label: "Per Siswa", icon: Users },
  { key: "kelas", label: "Per Kelas", icon: GraduationCap },
  { key: "lembaga", label: "Per Lembaga", icon: Buildings },
  { key: "nomenklatur", label: "Per Nomenklatur", icon: Tag },
];

const STATUS_META = {
  paid: { label: "LUNAS", cls: "bg-emerald-100 text-emerald-700" },
  partial: { label: "SEBAGIAN", cls: "bg-amber-100 text-amber-700" },
  unpaid: { label: "BELUM", cls: "bg-rose-100 text-rose-700" },
};

export default function RekapTagihan() {
  const [tab, setTab] = useState("siswa");
  const [lembagas, setLembagas] = useState([]);
  const [classes, setClasses] = useState([]);
  const [settings, setSettings] = useState({});
  const currentMonth = ym();
  const currentYear = new Date().getFullYear();
  // Tahun ajaran: kalau bulan >= Juli, tahun ajaran = current/current+1, else current-1/current
  const currentAcademicYear = new Date().getMonth() >= 6 ? currentYear : currentYear - 1;
  const [period, setPeriod] = useState(currentMonth);
  const [mode, setMode] = useState("monthly"); // 'monthly' | 'yearly' (khusus tab nomenklatur)
  const [academicYear, setAcademicYear] = useState(currentAcademicYear);
  const [filterLembaga, setFilterLembaga] = useState("all");
  const [filterKelas, setFilterKelas] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Range Juli tahun N s.d. Juni tahun N+1
  const yearlyRange = {
    period_start: `${academicYear}-07`,
    period_end: `${academicYear + 1}-06`,
  };
  const yearlyLabel = `TA ${academicYear}/${academicYear + 1} (Jul ${academicYear} – Jun ${academicYear + 1})`;

  useEffect(() => {
    api.get("/lembaga").then((l) => setLembagas(l.data)).catch(() => setLembagas([]));
  }, []);
  useEffect(() => {
    api.get("/siswa/classes" + (filterLembaga !== "all" ? `?lembaga_id=${filterLembaga}` : ""))
      .then((r) => {
        const list = r.data || [];
        setClasses(list);
        setFilterKelas((k) => (k === "all" || list.includes(k) ? k : "all"));
      })
      .catch(() => setClasses([]));
  }, [filterLembaga]);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (mode === "yearly") {
        q.set("period_start", yearlyRange.period_start);
        q.set("period_end", yearlyRange.period_end);
      } else if (period) {
        q.set("period", period);
      }
      if (filterLembaga !== "all") q.set("lembaga_id", filterLembaga);
      if (tab === "siswa" && filterKelas !== "all") q.set("class_name", filterKelas);
      const url = `/tagihan/rekap-${tab}${q.toString() ? "?" + q : ""}`;
      const [d, sett] = await Promise.all([api.get(url), api.get("/settings").catch(() => ({ data: {} }))]);
      setData(d.data);
      setSettings(sett.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat rekap"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab, period, filterLembaga, filterKelas, mode, academicYear]);

  const lembagaLabel = filterLembaga === "all" ? "Seluruh Lembaga" : (lembagas.find((l) => l.id === filterLembaga)?.name || "-");

  // === WhatsApp reminder helpers ===
  const normalizePhone = (raw) => {
    const d = String(raw || "").replace(/\D/g, "");
    if (!d) return "";
    if (d.startsWith("62")) return d;
    if (d.startsWith("0")) return "62" + d.slice(1);
    if (d.startsWith("8")) return "62" + d;
    return d;
  };

  const buildReminderText = (r) => {
    const lines = [
      `*PENGINGAT PEMBAYARAN*`,
      `Assalamu'alaikum Wr. Wb.`,
      ``,
      `Yth. Bapak/Ibu ${r.parent_name || "Wali Siswa"},`,
      ``,
      `Berikut informasi tagihan ananda:`,
      `👤 Nama: *${r.name}*`,
      `NIS: ${r.nis || "-"}`,
      `Kelas: ${r.class_name || "-"}`,
      `Lembaga: ${r.lembaga_name || "-"}`,
      `Periode: ${formatMonth(period)}`,
      ``,
      `Total Tagihan : ${formatIDR(r.total_tagihan)}`,
      `Sudah Dibayar : ${formatIDR(r.total_dibayar)}`,
      `*Sisa Tagihan : ${formatIDR(r.sisa)}*`,
      ``,
      `Mohon segera diselesaikan pembayarannya. Untuk cek riwayat & bukti bayar, silakan buka Portal Wali Murid.`,
      ``,
      `Terima kasih, Jazakumullahu khairan. 🙏`,
    ];
    return encodeURIComponent(lines.join("\n"));
  };

  const openWAReminder = (r) => {
    const phone = normalizePhone(r.parent_phone);
    if (!phone) return toast.warning(`No HP wali ${r.name} belum diisi`);
    const url = `https://wa.me/${phone}?text=${buildReminderText(r)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const sendBulkReminders = async () => {
    if (!data?.rows?.length) return;
    const targets = data.rows.filter((r) => r.sisa > 0 && normalizePhone(r.parent_phone));
    if (!targets.length) return toast.info("Tidak ada wali dengan sisa tagihan & no HP valid");
    const skipped = data.rows.filter((r) => r.sisa > 0 && !normalizePhone(r.parent_phone)).length;
    const msg = `Kirim reminder WA ke ${targets.length} wali murid (sisa tagihan > 0)?` + (skipped ? `\n(${skipped} dilewati karena no HP kosong)` : "") + `\n\nCatatan: Browser akan membuka ${targets.length} tab WhatsApp Web. Pastikan popup diizinkan.`;
    if (!window.confirm(msg)) return;
    toast.success(`Membuka ${targets.length} tab WhatsApp...`);
    for (let i = 0; i < targets.length; i++) {
      const r = targets[i];
      const phone = normalizePhone(r.parent_phone);
      const url = `https://wa.me/${phone}?text=${buildReminderText(r)}`;
      window.open(url, `_wa_${i}`, "noopener,noreferrer");
      // Delay 500ms between opens to reduce popup blocker rejects
      await new Promise((res) => setTimeout(res, 500));
    }
  };

  const printPDF = () => {
    if (!data) return;
    if (tab === "siswa") {
      generatePDF({
        title: `Rekap Tagihan Per Siswa`,
        subtitle: `${lembagaLabel}${filterKelas !== "all" ? " — Kelas " + filterKelas : ""} — ${formatMonth(period)}`,
        headers: ["NIS", "Nama Siswa", "Kelas", "Lembaga", "Tagihan", "Dibayar", "Sisa", "Status"],
        rows: data.rows.map((r) => [r.nis || "-", r.name, r.class_name || "-", r.lembaga_name || "-", formatIDR(r.total_tagihan), formatIDR(r.total_dibayar), formatIDR(r.sisa), STATUS_META[r.status]?.label || r.status]),
        meta: {
          "Periode": formatMonth(period), "Lembaga": lembagaLabel,
          "Jumlah Siswa": data.count,
          "Total Tagihan": formatIDR(data.total_tagihan),
          "Total Payment Masuk": formatIDR(data.total_dibayar),
          "Total Sisa": formatIDR(data.total_sisa),
        },
        filename: `rekap-tagihan-siswa-${period}.pdf`,
        settings,
        format: "a5",
      });
    } else if (tab === "kelas") {
      generatePDF({
        title: `Rekap Tagihan Per Kelas`,
        subtitle: `${lembagaLabel} — ${formatMonth(period)}`,
        headers: ["Lembaga", "Kelas", "Siswa", "Total Tagihan", "Dibayar", "Sisa", "%"],
        rows: data.rows.map((r) => [r.lembaga_name, r.class_name, r.siswa_count, formatIDR(r.total_tagihan), formatIDR(r.total_dibayar), formatIDR(r.sisa), r.total_tagihan > 0 ? `${Math.round((r.total_dibayar / r.total_tagihan) * 100)}%` : "0%"]),
        meta: { "Periode": formatMonth(period), "Lembaga": lembagaLabel, "Jumlah Kelas": data.count, "Total Tagihan": formatIDR(data.total_tagihan), "Total Payment Masuk": formatIDR(data.total_dibayar), "Total Sisa": formatIDR(data.total_sisa) },
        filename: `rekap-tagihan-kelas-${period}.pdf`,
        settings,
      });
    } else if (tab === "nomenklatur") {
      const label = mode === "yearly" ? yearlyLabel : formatMonth(period);
      generatePDF({
        title: `Rekap Tagihan Per Nomenklatur${mode === "yearly" ? " (Tahunan)" : ""}`,
        subtitle: `${lembagaLabel} — ${label}`,
        headers: ["Kode", "Nama Pos", "Jml Tagihan", "Total Tagihan", "Payment Masuk", "Sisa"],
        rows: data.rows.map((r) => [r.nomenklatur_code, r.nomenklatur_name || r.nomenklatur_code, r.tagihan_count, formatIDR(r.total_tagihan), formatIDR(r.total_dibayar), formatIDR(r.sisa)]),
        meta: {
          "Mode": mode === "yearly" ? "Tahun Ajaran" : "Bulanan",
          "Periode": label,
          "Lembaga": lembagaLabel,
          "Jumlah Pos": data.count,
          "Total Tagihan": formatIDR(data.total_tagihan),
          "Total Payment Masuk": formatIDR(data.total_dibayar),
          "Total Sisa": formatIDR(data.total_sisa),
        },
        filename: `rekap-nomenklatur-${mode === "yearly" ? `TA${academicYear}` : period}.pdf`,
        settings,
      });
    } else {
      generatePDF({
        title: `Rekap Tagihan Per Lembaga`,
        subtitle: `Yayasan — ${formatMonth(period)}`,
        headers: ["Lembaga", "Level", "Siswa", "Tagihan", "Dibayar", "Sisa", "%"],
        rows: data.rows.map((r) => [r.lembaga_name, r.level || "-", r.siswa_count, formatIDR(r.total_tagihan), formatIDR(r.total_dibayar), formatIDR(r.sisa), r.total_tagihan > 0 ? `${Math.round((r.total_dibayar / r.total_tagihan) * 100)}%` : "0%"]),
        meta: { "Periode": formatMonth(period), "Jumlah Lembaga": data.count, "Total Tagihan": formatIDR(data.total_tagihan), "Total Payment Masuk": formatIDR(data.total_dibayar), "Total Sisa": formatIDR(data.total_sisa) },
        filename: `rekap-tagihan-lembaga-${period}.pdf`,
        settings,
      });
    }
  };

  const exportExcel = () => {
    if (!data) return;
    let headers, rows;
    if (tab === "siswa") {
      headers = ["NIS", "NISN", "Nama", "Kelas", "Lembaga", "Wali", "HP Wali", "Total Tagihan", "Dibayar", "Sisa", "Status"];
      rows = data.rows.map((r) => [r.nis, r.nisn || "", r.name, r.class_name || "", r.lembaga_name, r.parent_name || "", r.parent_phone || "", r.total_tagihan, r.total_dibayar, r.sisa, STATUS_META[r.status]?.label || r.status]);
    } else if (tab === "kelas") {
      headers = ["Lembaga", "Kelas", "Jumlah Siswa", "Total Tagihan", "Dibayar", "Sisa"];
      rows = data.rows.map((r) => [r.lembaga_name, r.class_name, r.siswa_count, r.total_tagihan, r.total_dibayar, r.sisa]);
    } else if (tab === "nomenklatur") {
      headers = ["Kode", "Nama Pos", "Jenis", "Jml Tagihan", "Total Tagihan", "Payment Masuk", "Sisa"];
      rows = data.rows.map((r) => [r.nomenklatur_code, r.nomenklatur_name || "", r.kind || "", r.tagihan_count, r.total_tagihan, r.total_dibayar, r.sisa]);
    } else {
      headers = ["Lembaga", "Level", "Jumlah Siswa", "Jumlah Tagihan", "Total Tagihan", "Dibayar", "Sisa"];
      rows = data.rows.map((r) => [r.lembaga_name, r.level || "", r.siswa_count, r.tagihan_count, r.total_tagihan, r.total_dibayar, r.sisa]);
    }
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    XLSX.utils.book_append_sheet(wb, ws, "Rekap");
    XLSX.writeFile(wb, `rekap-tagihan-${tab}-${period}.xlsx`);
  };

  return (
    <div data-testid="rekap-tagihan-page">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Keuangan</div>
          <h1 className="font-display text-3xl font-black text-foreground tracking-tight">Rekap Tagihan</h1>
          <p className="text-muted-foreground text-sm mt-1">Rekap tagihan siap cetak — per siswa, per kelas, atau per lembaga.</p>
        </div>
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0B8D95] to-[#48B8BD] text-white px-4 py-2 rounded-lg shadow-sm" data-testid="rekap-ta-badge">
          <div className="text-[10px] uppercase tracking-wider opacity-80 leading-none">Tahun Ajaran Aktif</div>
          <div className="font-display text-lg font-black leading-none ml-2">{mode === "yearly" ? `${academicYear}/${academicYear + 1}` : `${currentAcademicYear}/${currentAcademicYear + 1}`}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex gap-1 border-b border-border" data-testid="rekap-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`rekap-tab-${t.key}`}
              className={`px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${tab === t.key ? "text-[#0B8D95] border-[#0B8D95]" : "text-muted-foreground border-transparent hover:text-foreground"}`}>
              <Icon size={16} weight={tab === t.key ? "duotone" : "regular"} />{t.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-3 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">Mode</Label>
          <div className="flex rounded-md border border-border overflow-hidden" data-testid="rekap-mode-toggle">
            <button onClick={() => setMode("monthly")} data-testid="rekap-mode-monthly"
              className={`px-3 py-1.5 text-xs font-bold ${mode === "monthly" ? "bg-[#0B8D95] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>Bulanan</button>
            <button onClick={() => setMode("yearly")} data-testid="rekap-mode-yearly"
              className={`px-3 py-1.5 text-xs font-bold ${mode === "yearly" ? "bg-[#0B8D95] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>Tahun Ajaran</button>
          </div>
        </div>
        {mode === "monthly" ? (
          <div><Label className="text-xs">Bulan</Label><Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-40" data-testid="rekap-period" /></div>
        ) : (
          <div><Label className="text-xs">Tahun Ajaran (Jul – Jun)</Label>
            <Select value={String(academicYear)} onValueChange={(v) => setAcademicYear(Number(v))}>
              <SelectTrigger className="w-56" data-testid="rekap-academic-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[currentYear - 3, currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
                  <SelectItem key={y} value={String(y)}>TA {y}/{y + 1} (Jul {y} – Jun {y + 1})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {tab !== "lembaga" && (
          <div><Label className="text-xs">Lembaga</Label>
            <Select value={filterLembaga} onValueChange={setFilterLembaga}>
              <SelectTrigger className="w-56" data-testid="rekap-lembaga"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Lembaga</SelectItem>{lembagas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {tab === "siswa" && (
          <div><Label className="text-xs">Kelas</Label>
            <Select value={filterKelas} onValueChange={setFilterKelas}>
              <SelectTrigger className="w-40" data-testid="rekap-kelas"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Kelas</SelectItem>{classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="ml-auto flex gap-2">
          {tab === "siswa" && (
            <Button variant="outline" size="sm" onClick={sendBulkReminders} data-testid="rekap-wa-bulk-btn" className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <WhatsappLogo size={14} weight="fill" />Kirim WA Massal
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={exportExcel} data-testid="rekap-excel-btn"><Download size={14} className="mr-1.5" />Excel</Button>
          <Button size="sm" onClick={printPDF} data-testid="rekap-pdf-btn"><Printer size={14} className="mr-1.5" />Cetak PDF</Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <SummaryCard label={tab === "siswa" ? "Siswa" : (tab === "kelas" ? "Kelas" : (tab === "nomenklatur" ? "Pos Nomen." : "Lembaga"))} value={data?.count || 0} testid="rekap-count" icon={Users} />
        <SummaryCard label="Total Tagihan" value={formatIDR(data?.total_tagihan || 0)} testid="rekap-total-tagihan" icon={Receipt} tone="info" />
        <SummaryCard label="Total Payment Masuk" value={formatIDR(data?.total_dibayar || 0)} testid="rekap-total-dibayar" tone="ok" />
        <SummaryCard label="Total Sisa" value={formatIDR(data?.total_sisa || 0)} testid="rekap-total-sisa" tone="danger" />
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground text-sm">Memuat rekap...</div>
        ) : !data?.rows?.length ? (
          <div className="p-10 text-center text-muted-foreground text-sm">Tidak ada data</div>
        ) : tab === "siswa" ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-3 font-bold">NIS</th>
                  <th className="px-3 py-3 font-bold">Nama Siswa</th>
                  <th className="px-3 py-3 font-bold">Kelas</th>
                  <th className="px-3 py-3 font-bold">Lembaga</th>
                  <th className="px-3 py-3 font-bold">Wali</th>
                  <th className="px-3 py-3 font-bold text-right">Tagihan</th>
                  <th className="px-3 py-3 font-bold text-right">Payment Masuk</th>
                  <th className="px-3 py-3 font-bold text-right">Sisa</th>
                  <th className="px-3 py-3 font-bold text-center">Status</th>
                  <th className="px-3 py-3 font-bold text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.siswa_id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-3 py-2.5 font-mono text-xs">{r.nis}</td>
                    <td className="px-3 py-2.5 font-semibold">{r.name}</td>
                    <td className="px-3 py-2.5">{r.class_name || "-"}</td>
                    <td className="px-3 py-2.5 text-xs">{r.lembaga_name}</td>
                    <td className="px-3 py-2.5 text-xs"><div>{r.parent_name || "-"}</div><div className="text-muted-foreground">{r.parent_phone || "-"}</div></td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold tabular-nums">{formatIDR(r.total_tagihan)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-600 tabular-nums">{formatIDR(r.total_dibayar)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-black tabular-nums text-rose-600">{formatIDR(r.sisa)}</td>
                    <td className="px-3 py-2.5 text-center"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black ${STATUS_META[r.status]?.cls}`}>{STATUS_META[r.status]?.label}</span></td>
                    <td className="px-3 py-2.5 text-right">
                      {r.sisa > 0 && (
                        <button onClick={() => openWAReminder(r)} className="p-1.5 hover:bg-emerald-50 rounded text-emerald-600" title="Kirim reminder WA" data-testid={`rekap-wa-${r.siswa_id}`}><WhatsappLogo size={14} weight="fill" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : tab === "nomenklatur" ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rekap-nomenklatur-table">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-bold">Kode</th>
                  <th className="px-4 py-3 font-bold">Nama Pos</th>
                  <th className="px-4 py-3 font-bold">Jenis</th>
                  <th className="px-4 py-3 font-bold text-center">Jml Tagihan</th>
                  <th className="px-4 py-3 font-bold text-right">Total Tagihan</th>
                  <th className="px-4 py-3 font-bold text-right">Payment Masuk</th>
                  <th className="px-4 py-3 font-bold text-right">Sisa</th>
                  <th className="px-4 py-3 font-bold text-center">Progress</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const pct = r.total_tagihan > 0 ? Math.round((r.total_dibayar / r.total_tagihan) * 100) : 0;
                  const kindCls = r.kind === "income" ? "bg-emerald-100 text-emerald-700" : r.kind === "expense" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600";
                  return (
                    <tr key={r.nomenklatur_code} className="border-t border-border hover:bg-muted/30" data-testid={`rekap-nom-row-${r.nomenklatur_code}`}>
                      <td className="px-4 py-2.5 font-mono font-bold text-[#0B8D95]">{r.nomenklatur_code}</td>
                      <td className="px-4 py-2.5 font-semibold">{r.nomenklatur_name || "-"}</td>
                      <td className="px-4 py-2.5"><span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${kindCls}`}>{r.kind || "-"}</span></td>
                      <td className="px-4 py-2.5 text-center font-mono">{r.tagihan_count}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-bold tabular-nums">{formatIDR(r.total_tagihan)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-emerald-600 tabular-nums">{formatIDR(r.total_dibayar)}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-black tabular-nums text-rose-600">{formatIDR(r.sisa)}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} /></div>
                          <span className="text-xs font-bold tabular-nums w-10 text-right">{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-muted/50 font-black">
                <tr>
                  <td className="px-4 py-3" colSpan={3}>TOTAL</td>
                  <td className="px-4 py-3 text-center font-mono">{data.rows.reduce((a, r) => a + (r.tagihan_count || 0), 0)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{formatIDR(data.total_tagihan)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-emerald-700">{formatIDR(data.total_dibayar)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-rose-700">{formatIDR(data.total_sisa)}</td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tfoot>
            </table>
            {(data.total_unallocated || 0) > 0 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2" data-testid="rekap-unalloc-hint">
                <Warning size={16} weight="fill" className="mt-0.5 flex-shrink-0" />
                <div>
                  <b>Info:</b> Ada {formatIDR(data.total_unallocated)} pembayaran belum ter-link ke tagihan. Buka menu <b>Pembayaran</b> → tombol <b>Rematch</b> untuk auto-link.
                </div>
              </div>
            )}
          </div>
        ) : tab === "kelas" ? (
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-bold">Lembaga</th>
                <th className="px-4 py-3 font-bold">Kelas</th>
                <th className="px-4 py-3 font-bold text-center">Siswa</th>
                <th className="px-4 py-3 font-bold text-right">Tagihan</th>
                <th className="px-4 py-3 font-bold text-right">Payment Masuk</th>
                <th className="px-4 py-3 font-bold text-right">Sisa</th>
                <th className="px-4 py-3 font-bold text-center">Progress</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => {
                const pct = r.total_tagihan > 0 ? Math.round((r.total_dibayar / r.total_tagihan) * 100) : 0;
                return (
                  <tr key={i} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-2.5 font-semibold">{r.lembaga_name}</td>
                    <td className="px-4 py-2.5">{r.class_name}</td>
                    <td className="px-4 py-2.5 text-center font-mono">{r.siswa_count}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold tabular-nums">{formatIDR(r.total_tagihan)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-emerald-600 tabular-nums">{formatIDR(r.total_dibayar)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-black tabular-nums text-rose-600">{formatIDR(r.sisa)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} /></div>
                        <span className="text-xs font-bold tabular-nums w-10 text-right">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-bold">Lembaga</th>
                <th className="px-4 py-3 font-bold">Level</th>
                <th className="px-4 py-3 font-bold text-center">Siswa</th>
                <th className="px-4 py-3 font-bold text-center">Jml Tagihan</th>
                <th className="px-4 py-3 font-bold text-right">Total Tagihan</th>
                <th className="px-4 py-3 font-bold text-right">Payment Masuk</th>
                <th className="px-4 py-3 font-bold text-right">Sisa</th>
                <th className="px-4 py-3 font-bold text-center">Progress</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const pct = r.total_tagihan > 0 ? Math.round((r.total_dibayar / r.total_tagihan) * 100) : 0;
                return (
                  <tr key={r.lembaga_id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-2.5 font-semibold">{r.lembaga_name}</td>
                    <td className="px-4 py-2.5"><span className="inline-block px-2 py-0.5 rounded bg-[#0B8D95]/10 text-[#0B8D95] text-xs font-bold">{r.level || "-"}</span></td>
                    <td className="px-4 py-2.5 text-center font-mono">{r.siswa_count}</td>
                    <td className="px-4 py-2.5 text-center font-mono">{r.tagihan_count}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold tabular-nums">{formatIDR(r.total_tagihan)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-emerald-600 tabular-nums">{formatIDR(r.total_dibayar)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-black tabular-nums text-rose-600">{formatIDR(r.sisa)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} /></div>
                        <span className="text-xs font-bold tabular-nums w-10 text-right">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, testid, icon: Icon, tone = "default" }) {
  const tones = {
    default: "bg-card border-border",
    info: "bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800",
    ok: "bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800",
    danger: "bg-rose-50 border-rose-200 dark:bg-rose-900/20 dark:border-rose-800",
    warn: "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
        {Icon && <Icon size={16} weight="duotone" className="text-foreground/60" />}
      </div>
      <div className="font-display text-xl md:text-2xl font-black text-foreground tracking-tight tabular-nums">{value}</div>
    </div>
  );
}
